import { Component, Inject, OnInit } from "@angular/core";
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from "@angular/material/dialog";
import { optionValidator, ReaderService } from "../../services/reader.service";
import { UntypedFormControl, Validators } from "@angular/forms";
import {
  Messages,
  Propulsion,
  Region,
  Vehicle,
} from "../utility/utility-classes";
import { WriterService } from "../../services/writer.service";
import { AuthenticationService } from "../authentication/authentication/authentication.service";
import { ErrorHandlerDialogComponent } from "../error-handler-dialog/error-handler-dialog.component";
import { map, Observable, startWith } from "rxjs";
import { ConfirmDeletionDialogComponent } from "../utility/confirm-deletion-dialog/confirm-deletion-dialog.component";

@Component({
    selector: "app-new-propulsion",
    templateUrl: "./new-propulsion.component.html",
    styleUrls: ["./new-propulsion.component.scss"],
    standalone: false
})
export class NewPropulsionComponent implements OnInit {
  vehicle: Vehicle;
  propulsion: Propulsion = new Propulsion();
  propulsions: string[];
  oems: string[];
  brands: string[];
  statuses: string[];
  bodystyles: string[];
  regions: Region[] = [];
  drivetypes: string[];
  propulsionControl = new UntypedFormControl("", [Validators.required]);
  oemControl = new UntypedFormControl("", [Validators.required]);
  brandControl = new UntypedFormControl("", [Validators.required]);
  namePlateControl = new UntypedFormControl("", [Validators.required]);
  statusControl = new UntypedFormControl("", [Validators.required]);
  bodystyleControl = new UntypedFormControl("", [Validators.required]);
  driveTypeControl = new UntypedFormControl("");
  evEpaControl = new UntypedFormControl("");
  evWltpControl = new UntypedFormControl("");
  evCltcControl = new UntypedFormControl("");

  sop = new UntypedFormControl("", [
    Validators.required,
    Validators.pattern("[0-9]{4}"),
  ]);
  eop = new UntypedFormControl("", [
    Validators.required,
    Validators.pattern("[0-9]{4}"),
  ]);
  minStartYear = 1980;
  regionControl = false;
  filteredOEMs: Observable<string[]>;
  filteredBrands: Observable<string[]>;

  vehicleControl = new UntypedFormControl("", [Validators.required]);
  filteredVehicles: Observable<string[]>;
  vehicles: string[];

  editMode: false;

  enableEvEpa: boolean;
  enableEvWltp: boolean;
  enableEvCltc: boolean;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    public dialogRef: MatDialogRef<NewPropulsionComponent>,
    public confirmDialog: MatDialogRef<ConfirmDeletionDialogComponent>,
    private readonly dialog: MatDialog,
    private readonly authService: AuthenticationService,
    private readonly readerService: ReaderService,
    private readonly writerService: WriterService
  ) {
    this.editMode = data.editMode;
    if (data.entry != null) {
      if (data.entry.vehicle != null) {
        this.vehicle = data.entry.vehicle;
      }
      if (data.entry.record != null) {
        this.propulsion = data.entry.record;
        this.regions = data.entry.regions;
      }
    }
  }

  ngOnInit(): void {
    if (this.propulsion.startofproduction === 1111) {
      this.minStartYear = 1111;
    }
    if (this.propulsion.startofproduction === 1980) {
      this.minStartYear = 1979;
    }
    this.oemControl.disable();
    this.propulsionControl.markAllAsTouched();
    this.brandControl.markAllAsTouched();
    this.statusControl.markAllAsTouched();
    this.bodystyleControl.markAllAsTouched();
    this.driveTypeControl.markAllAsTouched();
    this.namePlateControl.markAllAsTouched();
    this.sop.markAllAsTouched();
    this.eop.markAllAsTouched();
    this.evEpaControl.markAllAsTouched();
    this.evWltpControl.markAllAsTouched();
    this.evCltcControl.markAllAsTouched();
    this.propulsion.dedicated = this.propulsion.dedicated === "Yes";

    this.readerService.getAllEnabledFromTable("oem").subscribe((x: any[]) => {
      this.oems = x.map((obj) => obj.name);
      this.oemControl.setValidators(optionValidator(this.oems));
      this.filteredOEMs = this.oemControl.valueChanges.pipe(
        startWith(""),
        map((value) => this.readerService._filterAutoComplete(value, this.oems))
      );
    });
    this.readerService.getAllEnabledFromTable("brand").subscribe((x: any[]) => {
      this.brands = x.map((obj) => obj.name);
      this.brandControl.setValidators(optionValidator(this.brands));
      this.filteredBrands = this.brandControl.valueChanges.pipe(
        startWith(""),
        map((value) =>
          this.readerService._filterAutoComplete(value, this.brands)
        )
      );
    });
    const regMap: Map<string, Region> = new Map(
      this.regions.map((reg) => [reg.regionname, reg])
    );
    this.readerService.getRegions("propulsion").subscribe((x: any[]) => {
      this.regions = x.map((y) => {
        const exists: boolean = regMap.has(y.split(",")[1]);
        const xx: Region = regMap.get(y.split(",")[1]);
        return new Region(
          this.vehicle.id,
          y.split(",")[1],
          exists ? xx.value || "X" : null
        );
      });
      this.checkForEvRanges();
    });
    this.readerService
      .getAllEnabledFromTable("propulsiontype")
      .subscribe((x: any[]) => {
        this.propulsions = x.map((obj) => obj.name);
      });
    this.readerService
      .getAllEnabledFromTable("status")
      .subscribe((x: any[]) => {
        this.statuses = x.map((obj) => obj.name);
      });
    this.readerService
      .getAllEnabledFromTable("bodystylevalue")
      .subscribe((x: any[]) => {
        this.bodystyles = x.map((obj) => obj.name);
      });
    this.readerService
      .getAllEnabledFromTable("driveconfig")
      .subscribe((x: any[]) => {
        this.drivetypes = x.map((obj) => obj.name);
      });

    // Only required when creating entry from homepage.
    if (this.vehicle === null) {
      this.vehicle = new Vehicle();
      this.readerService.getDistinctVehicles().subscribe((x) => {
        this.vehicles = x.map((obj) => obj.id + " - " + obj.nameplate);
        this.vehicleControl.setValidators(optionValidator(this.vehicles));
        this.filteredVehicles = this.vehicleControl.valueChanges.pipe(
          startWith(""),
          map((value) =>
            this.readerService._filterAutoComplete(value, this.vehicles)
          )
        );
      });
    } else {
      this.driveTypeControl.setValue(this.vehicle.driveconfig);
      this.oemControl.setValue(this.vehicle.oem);
      this.brandControl.setValue(this.vehicle.brand);
      if (this.propulsion != null) {
        this.bodystyleControl.setValue(this.propulsion.bodystyle);
        this.statusControl.setValue(this.propulsion.status);
        this.propulsionControl.setValue(this.propulsion.type);
        this.driveTypeControl.setValue(this.propulsion.drivetype);
        this.sop.setValue(this.propulsion.startofproduction);
        this.eop.setValue(this.propulsion.endofproduction);
      }
    }
  }
  public validateUserEntry(): void {
    Object.keys(this.propulsion).forEach((key: string) => {
      if (this.propulsion[key] && typeof this.propulsion[key] === "string") {
        this.propulsion[key] = this.propulsion[key].trim();
        //if its just an empty string, set it to null
        if (!this.propulsion[key]) {
          this.propulsion[key] = null;
        }
      }
    });
  }

  public save(): void {
    this.validateUserEntry();
    this.propulsion.vehicleid = this.vehicle.id;
    this.propulsion.type = this.propulsionControl.value;
    this.propulsion.brand = this.brandControl.value;
    this.propulsion.status = this.statusControl.value;
    this.propulsion.bodystyle = this.bodystyleControl.value;
    this.propulsion.drivetype = this.driveTypeControl.value;
    this.propulsion.startofproduction = this.sop.value;
    this.propulsion.endofproduction = this.eop.value;

    this.propulsion.created = new Date().toISOString();
    this.propulsion.createdby = this.authService.userInfo.userName;

    if (!this.editMode) {
      this.propulsion.id = undefined;
    }

    this.writerService
      .processRecord(this.propulsion, "propulsion", "POST")
      .subscribe({
        next: (response: any) => {
          this.propulsion.id = response.id;
          this.regions.forEach((region: Region) => {
            region.propulsionid = this.propulsion.id;
            region.created = this.propulsion.created;
          });
          const xregions: Region[] = this.regions.filter(
            (region: Region) =>
              region.details != null && region.details.trim() !== ""
          );
          xregions.forEach((region: Region) => {
            if (region.details.trim() == "x") {
              region.details = "X";
            }
          });
          this.writerService
            .processRecord(xregions, "propulsion_region", "POST")
            .subscribe(
              (complete) => {
                this.dialog.open(ErrorHandlerDialogComponent, {
                  data: { message: Messages.successRefresh, status: "Success" },
                });
                this.dialogRef.close();
              },
              (error) => {
                this.dialog.open(ErrorHandlerDialogComponent, {
                  data: {
                    message:
                      "Error saving Propulsion Regions </br>" + error.error ==
                      null
                        ? error.message
                        : error.error.message,
                    status:
                      error.error == null ? error.status : error.error.status,
                  },
                });
              }
            );
        },
        error: (error: any) => {
          this.dialog.open(ErrorHandlerDialogComponent, {
            data: {
              message:
                "Error saving Propulsion Record </br>" + error.error == null
                  ? error.message
                  : error.error.message,
              status: error.error == null ? error.status : error.error.status,
            },
          });
        },
      });
  }

  public delete(): void {
    this.confirmDialog = this.dialog.open(ConfirmDeletionDialogComponent, {
      data: {
        recordId: this.propulsion.id,
        recordName: this.propulsion.type,
        recordType: "propulsion",
        vehicleId: this.propulsion.vehicleid,
      },
    });

    this.confirmDialog.afterClosed().subscribe((result) => {
      if (result) {
        this.dialogRef.close();
      }
    });
  }

  public isValid(): boolean {
    this.regionControl = false;
    for (const region of this.regions) {
      if (region.details != null && region.details !== "") {
        this.regionControl = true;
      }
    }
    return !(
      this.propulsionControl.invalid ||
      this.statusControl.invalid ||
      this.bodystyleControl.invalid ||
      this.sop.invalid ||
      this.eop.invalid ||
      !this.regionControl
    );
  }

  public updateVehicle(): void {
    const vehicle = String(this.vehicleControl.value).split("-");
    if (vehicle[0] != null && vehicle[0] !== "") {
      this.readerService.getVehicleData(vehicle[0]).subscribe((x) => {
        this.vehicle = x;
        this.propulsion.nameplate = this.vehicle.nameplate;
        this.driveTypeControl.setValue(this.vehicle.driveconfig);
        this.oemControl.setValue(this.vehicle.oem);
        this.brandControl.setValue(this.vehicle.brand);
      });
    }
  }

  public checkForEvRanges(): void {
    this.regions.forEach((region) => {
      if (region.region === "North America") {
        if (this.isRegionSelected(region)) {
          this.enableEvEpa = true;
        } else {
          this.enableEvEpa = false;
          this.evEpaControl.setValue("");
        }
      }
      if (region.region === "Europe") {
        if (this.isRegionSelected(region)) {
          this.enableEvWltp = true;
        } else {
          this.evWltpControl.setValue(null);
          this.enableEvWltp = false;
        }
      }
      if (region.region === "China") {
        if (this.isRegionSelected(region)) {
          this.enableEvCltc = true;
        } else {
          this.evCltcControl.setValue(null);
          this.enableEvCltc = false;
        }
      }
    });
  }

  public isRegionSelected(region: Region): boolean {
    return (
      (region.details != null && region.details.length > 0) ||
      (region.value != null && region.value > 0)
    );
  }

  public validateYear(): void {
    if (this.sop.value === 1111) {
      this.minStartYear = 1111;
    }
    if (this.sop.value === 1112) {
      this.sop.setValue(1980);
    }
    if (this.sop.value === 1979) {
      this.sop.setValue(1111);
      this.minStartYear = 1111;
    }
    if (this.sop.value === 1980) {
      this.minStartYear = 1979;
    }
  }
}
